"use client"

import { useQuery } from "@tanstack/react-query"
import { api } from "@edemics/lib/api"

/**
 * Sensible polling window for header notifications — 30s while the tab
 * is focused. Matches the chat unread poll cadence (see
 * @edemics/contexts/chat-context) so both counts refresh in step and
 * users can't game one against the other.
 */
export const NOTIFICATIONS_POLL_INTERVAL_MS = 30_000

export interface NotificationRow {
  id: string
  title: string
  message: string
  createdAt: string
  read: boolean
  type?: string
  actor?: {
    id?: string
    name?: string
    avatar?: string
  }
  metadata?: Record<string, unknown>
}

interface NotificationsResponse {
  items: NotificationRow[]
  unreadCount: number
  total: number
}

/**
 * Header notification poll — returns the latest N notifications for the
 * caller + the unread count. The BE endpoint at `/notifications` is
 * expected to return either an array (older shape) or an object with
 * `{ items, unreadCount, total }`; the hook normalises both.
 *
 * Caller-side unread count is preferred (fewer roundtrips) but we fall
 * back to counting `read === false` on the returned rows if the BE
 * doesn't emit an unreadCount field yet.
 */
export function useNotificationsPoll({
  enabled = true,
  limit = 10,
}: {
  enabled?: boolean
  limit?: number
} = {}) {
  return useQuery<NotificationsResponse>({
    queryKey: ["notifications", "poll", limit],
    queryFn: async () => {
      const { data } = await api.get("/notifications", {
        params: { limit, unreadOnly: false },
      })
      // Normalise both response shapes.
      let items: NotificationRow[] = []
      let unreadCount = 0
      let total = 0
      if (Array.isArray(data)) {
        items = data as NotificationRow[]
        unreadCount = items.filter((n) => !n.read).length
        total = items.length
      } else if (data && Array.isArray(data.items)) {
        items = data.items
        unreadCount =
          typeof data.unreadCount === "number"
            ? data.unreadCount
            : items.filter((n) => !n.read).length
        total = typeof data.total === "number" ? data.total : items.length
      }
      return { items, unreadCount, total }
    },
    enabled,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: NOTIFICATIONS_POLL_INTERVAL_MS - 1_000,
    retry: 1,
  })
}
